import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
// F3-1 / denetim #29: raw axios → apiClient (401-refresh-retry interceptor)
import axios from '../utils/api';
import { useTranslation } from 'react-i18next';
import { broadcastAuthEvent, AUTH_EVENT_KEY } from '../utils/api';
import { isPrerendering } from '../utils/isPrerender';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const normalizeUser = (data) => {
  if (!data) return data;
  const normalized = { ...data };
  if (normalized.unlimitedAccessUntil !== undefined && normalized.unlimited_access_until === undefined) {
    normalized.unlimited_access_until = normalized.unlimitedAccessUntil;
  }
  if (normalized.marketingConsent !== undefined && normalized.marketing_consent === undefined) {
    normalized.marketing_consent = normalized.marketingConsent;
  }
  if (normalized.createdAt !== undefined && normalized.created_at === undefined) {
    normalized.created_at = normalized.createdAt;
  }
  if (normalized.updatedAt !== undefined && normalized.updated_at === undefined) {
    normalized.updated_at = normalized.updatedAt;
  }
  return normalized;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { i18n } = useTranslation();

  // Prevent StrictMode double-mount from firing checkAuthStatus twice
  const authCheckRef = useRef(false);
  // Son /auth/me zamanı — pencere odağına dönüşte bayat profil yenilenir
  // (premium satın alma/webhook sonrası header'ın refresh beklemeden
  // "Premium Aktif"e dönmesi için).
  const lastAuthRefreshRef = useRef(0);
  const AUTH_REFRESH_INTERVAL_MS = 60 * 1000;

  // F3-1: API_URL doğrudan kullanılmıyor (apiClient baseURL'i constants'dan
  // alıyor) — yerel sabit kaldırıldı; gerektiğinde API_BASE_URL import'u kullanılır.

  // F3-1: axios artık utils/api'daki paylaşılan apiClient (baseURL ve
  // withCredentials orada zaten yapılandırılmış) — defaults mutasyonu kaldırıldı.

  // Cross-tab auth sync via a non-sensitive event key. The JWT itself lives in
  // an HttpOnly cookie (shared across tabs by the browser), so we never read it
  // from JS. We only listen for login/logout events broadcast by other tabs.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== AUTH_EVENT_KEY || !e.newValue) return;

      let event;
      try {
        event = JSON.parse(e.newValue);
      } catch (_) {
        return;
      }

      if (event?.type === 'logout') {
        setUser(null);
        setIsAuthenticated(false);
        // Kayıt Duvarı modeli: logout'ta /login'e değil ana sayfaya (misafir)
        // dön — '/' misafirken kimlik doğrulamalı istek tetiklemez, döngü yok.
        window.location.href = '/';
      } else if (event?.type === 'login') {
        // Another tab signed in — re-validate the session via the cookie.
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user is authenticated on app load
  useEffect(() => {
    if (authCheckRef.current) return;
    authCheckRef.current = true;
    // ReactSnap prerendering esnasında canlı backend'e istek atma
    if (isPrerendering()) {
      setIsLoading(false);
      return;
    }
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      // With HttpOnly cookies, we just call /auth/me — the cookie is sent automatically.
      // _skipGlobalLogout: misafirde 401 + refresh başarısızlığı normal bir
      // durumdur; kullanıcıyı /login'e boot etmek yerine misafir olarak bırak.
      const response = await axios.get('/auth/me', { _skipGlobalLogout: true });
      
      const userData = normalizeUser(response.data.user || response.data);
      
      setUser(userData);
      setIsAuthenticated(true);
      lastAuthRefreshRef.current = Date.now();
      
      // Language sync: localStorage has priority over DB.
      const localStorageLang = localStorage.getItem('preferred-language')
                            || localStorage.getItem('i18nextLng');
      const userLanguage = userData.preferred_language || 'de';
      
      if (localStorageLang && localStorageLang !== userLanguage) {
        try {
          await axios.put('/languages/user/preference', {
            language: localStorageLang
          });
        } catch (error) {
          console.error('Failed to sync language to DB:', error.response?.data || error.message);
        }
        
        if (i18n.language !== localStorageLang) {
          i18n.changeLanguage(localStorageLang);
        }
      } else {
        if (i18n.language !== userLanguage) {
          i18n.changeLanguage(userLanguage);
          localStorage.setItem('preferred-language', userLanguage);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, oauthData = null) => {
    try {
      let response;
      
      if (oauthData && oauthData.isOAuth) {
        const { user: oauthUser } = oauthData;
        
        const normalizedUser = normalizeUser(oauthUser);

        setUser(normalizedUser);
        setIsAuthenticated(true);
        broadcastAuthEvent('login');
        
        return { user: normalizedUser };
      } else {
        response = await axios.post('/auth/login', {
          email,
          password
        });

        const { user: loginUser } = response.data;
        
        const normalizedUser = normalizeUser(loginUser);

        setUser(normalizedUser);
        setIsAuthenticated(true);
        broadcastAuthEvent('login');
        
        return response.data;
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'Giriş yapılırken bir hata oluştu'
      );
    }
  };

  const register = async (userData) => {
    try {
      let response;
      
      if (userData.isOAuth) {
        const { user: oauthUser } = userData;

        const normalizedUser = normalizeUser(oauthUser);

        setUser(normalizedUser);
        setIsAuthenticated(true);
        broadcastAuthEvent('login');
        
        return { user: normalizedUser };
      } else {
        response = await axios.post('/auth/register', userData);
        
        const { user: regUser, requiresEmailVerification } = response.data;
        
        const normalizedUser = normalizeUser(regUser);

        if (requiresEmailVerification) {
          setUser(normalizedUser);
          setIsAuthenticated(false);
          localStorage.setItem('newUserAwaitingVerification', 'true');
        } else {
          setUser(normalizedUser);
          setIsAuthenticated(true);
          broadcastAuthEvent('login');
        }
        
        return { 
          user: normalizedUser, 
          requiresEmailVerification: requiresEmailVerification || false 
        };
      }
    } catch (error) {
      console.error('Registration failed:', error);

      // express-validator hataları: { success:false, message:'Validation failed',
      // errors:[{msg, param, ...}] } — ilk validation mesajını yüzeye çıkar,
      // yoksa kullanıcı sadece jenerik "Validation failed" görüyor.
      const errData = error.response?.data;
      const firstValidationError = Array.isArray(errData?.errors) && errData.errors.length
        ? errData.errors[0]?.msg
        : null;

      throw new Error(
        firstValidationError ||
        errData?.message ||
        'Kayıt olurken bir hata oluştu'
      );
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.warn('Server-side logout failed:', error.message);
    }

    // Notify other tabs to log out as well (non-sensitive event).
    broadcastAuthEvent('logout');

    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(prevUser => normalizeUser({
      ...prevUser,
      ...userData
    }));
  };

  const refreshUserData = async () => {
    try {
      const response = await axios.get('/auth/me', { _skipGlobalLogout: true });
      setUser(normalizeUser(response.data.user));
      lastAuthRefreshRef.current = Date.now();
      return true;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return false;
    }
  };

  // Pencere/sekme tekrar odağı kazandığında (ve mobilde görünürlüğe
  // dönüldüğünde) profili sessizce tazele. Örn. kullanıcı PayPal'dan
  // dönüp premium satın aldıysa ya da hesabına manuel premium verildiyse
  // header "Get unlimited" → "Premium Aktif"e sayfa refresh'i olmadan döner.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const revalidate = () => {
      if (Date.now() - lastAuthRefreshRef.current < AUTH_REFRESH_INTERVAL_MS) return;
      refreshUserData();
    };
    const handleFocus = () => revalidate();
    const handleVisibility = () => { if (document.visibilityState === 'visible') revalidate(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alias for backward compatibility
  const refreshUser = refreshUserData;

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'Şifre sıfırlama e-postası gönderilirken bir hata oluştu'
      );
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axios.post('/auth/reset-password', {
        token,
        password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Reset password failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'Şifre sıfırlanırken bir hata oluştu'
      );
    }
  };

  const loginWithToken = (userData) => {
    setUser(normalizeUser(userData));
    setIsAuthenticated(true);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    setIsAuthenticated,
    login,
    loginWithToken,
    register,
    logout,
    updateUser,
    refreshUserData,
    refreshUser,
    forgotPassword,
    resetPassword,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};